/**
 * # AsErrorInfo Component
 * 
 * A component for displaying error information to users in a structured format.
 * This component displays error details, timestamps, and provides copy and undo functionality.
 * 
 * ## Features
 * - Displays detailed error information in a clean, structured format
 * - Copy to clipboard functionality for error details
 * - Undo functionality to revert to previous states
 * - Styled error panel with customizable accent color
 * 
 * ## Component Hierarchy
 * ```
 * AsErrorInfo (as-error-info)
 * ├── as-panel-header - Header section using accent color
 * ├── .error-description - Descriptive text about the error
 * ├── .error-info - Container for error details
 * │   ├── .error-info-content - Rows of error information
 * │   └── as-text-button.error-copy-button - Button to copy error details
 * └── as-text-button.error-undo-button - Button to undo to previous state
 * ```
 * 
 * ## CSS Custom Properties
 * - `--error-header-accent-col`: Accent color for the error panel header (default: #ca2222)
 * 
 * ## Events
 * ### Listened Events
 * - `as-intrn-system-info`: Receives system information to display
 * - `as-intrn-new-error`: Receives new error information to display
 * 
 * ## Methods
 * - `updateErrorInfo()`: Updates the displayed error information
 */

import '../panels/as-panel.ts'
import '../panels/subcomps/as-panel-header.ts'
import { AsTextButton } from '../inputs/buttons/as-text-button.ts';
import { AsTooltip } from '../overlays/as-tooltip.ts';
import { devlog } from '../../util/utils.ts';
import styleText from './as-error-info.css?raw';
import { sendEv } from '../../data/events.ts';

const errorRow = (rowKey: string, rowValue: string) => /*html*/`
<div class="error-row">
  <as-text class="error-label-text" dict-key="errorPanel.label.${rowKey}">
    ${rowKey}
  </as-text>
  :
  <expand-flex></expand-flex>
  <span class="value-text">
    ${rowValue}
  </span>
</div>
`;

class AsErrorInfo extends HTMLElement {
  static readonly tag = 'as-error-info' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private _tooltipInfo: AsTooltip | null = null;
  private _tooltipResponse: AsTooltip | null = null;
  private _copyButton: AsTextButton | null = null;
  private _undoButton: AsTextButton | null = null;

  private _errorInfo: {
    timestamp: string;
    sessionId: string;
    requestId: string | undefined;
    error: Error;
    info: string | undefined;
    serverCode: string;
  } = {
    timestamp: new Date().toUTCString(),
    sessionId: '...',
    requestId: undefined,
    error: new Error('empty error'),
    info: undefined,
    serverCode: '-1'
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsErrorInfo._sheet
    ];
    this.init(); // Initial rendering
  }

  connectedCallback() {
    this.setupEventListeners();
    // devlog("AS-ERROR-INFO: Getting system info");
    sendEv(window, 'as-intrn-get-system-info', { detail: { requester: this } });
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init = () => {
    this.render();

  }

  rerender = () => {
    this.render();
    this.setupEventListeners(); // Reattach listeners after rendering
  }

  render = () => {
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = /*html*/`
        <as-panel-header> 
          <as-text dict-key="Base.ErrorHeader">
            Quite complex stuff...
          </as-text>
        </as-panel-header>

        <as-text class="error-description" dict-key="Base.ErrorInfo.ErrorDescription.Generic">
          We are very sorry! But something went wrong.\n
          You can copy the following information and send it to the support team.\n
          Press Undo to get back to a previous working state.
        </as-text>
        <div class="error-info">
          <div class="error-info-content">
            ${Object.entries(this._errorInfo).map(([key, value]) =>
              (value) && errorRow(key, value.toString())
            ).join('')}
          </div>
          
          <as-text-button class="error-copy-button">
            <as-text dict-key="Base.ErrorPanel.Button.CopyToClipboard">
              Copy
            </as-text>
          </as-text-button>

        </div> <!-- error-info -->

        <as-text-button class="error-undo-button">
          <as-text dict-key="Base.ErrorPanel.Button.Undo">
            Undo
          </as-text>
        </as-text-button>
      `;
      this._tooltipInfo = this.shadowRoot!.querySelector('as-tooltip.info') as unknown as AsTooltip;
      this._tooltipResponse = this.shadowRoot!.querySelector('as-tooltip.response') as unknown as AsTooltip;
      this._copyButton = this.shadowRoot!.querySelector('.error-copy-button') as AsTextButton;
      this._undoButton = this.shadowRoot!.querySelector('.error-undo-button') as AsTextButton;
    }
  }

  setupEventListeners = () => {
    const copyButton = this.shadowRoot!.querySelector('.error-copy-button') as AsTextButton;
    const undoButton = this.shadowRoot!.querySelector('.error-undo-button') as AsTextButton;

    this.removeEventListeners(); // Remove any existing listeners

    if (copyButton) {
      copyButton.addEventListener('click', this.handleCopyButtonClick);
    }
    if (undoButton) {
      undoButton.addEventListener('click', this.handleUndoButtonClick);
    }

    this.addEventListener('as-intrn-system-info', this.handleSystemInfo);
    this.addEventListener('as-intrn-new-error', this.handleNewError);
  }

  removeEventListeners = () => {
    const copyButton = this.shadowRoot!.querySelector('.error-copy-button') as AsTextButton;
    const undoButton = this.shadowRoot!.querySelector('.error-undo-button') as AsTextButton;

    if (copyButton) {
      copyButton.removeEventListener('click', this.handleCopyButtonClick);
    }
    if (undoButton) {
      undoButton.removeEventListener('click', this.handleUndoButtonClick);
    }

    this.removeEventListener('as-intrn-system-info', this.handleSystemInfo);
    this.removeEventListener('as-intrn-new-error', this.handleNewError);
  }

  handleCopyButtonClick = (event: Event) => {
    devlog("Copy button clicked", event);
    const errorJson = JSON.stringify(this._errorInfo, null, 2);
    navigator.clipboard.writeText(errorJson);
    devlog("Flashing tooltip", this._tooltipResponse);
    this._copyButton?.showResponse({textKey: 'Base.Actions.Copy.Success', defaultText: 'Copied!'});
  }

  handleUndoButtonClick = () => {
    devlog("Undo button clicked");
    // Add your undo logic here
  }

  handleSystemInfo = (event: CustomEvent) => {
    // devlog("Got system info", event.detail);
    for (const [key, value] of Object.entries(event.detail.info)) {
      this._errorInfo[key] = value;
    }
    this.rerender(); // Use rerender to update the component
  }

  handleNewError = (event: CustomEvent) => {
    devlog("New error", event.detail);
    this.updateErrorInfo(event.detail);
    this.rerender(); // Use rerender to update the component
  }

  updateErrorInfo = (newErrorInfo: Partial<typeof this._errorInfo>) => {
    this._errorInfo = {
      ...this._errorInfo,
      ...newErrorInfo,
      timestamp: new Date().toUTCString()
    };
  }
}

// Check if the element is already defined before registering it
if (!customElements.get(AsErrorInfo.tag)) {
  customElements.define(AsErrorInfo.tag, AsErrorInfo);
} else {
  console.warn("as-error-info already defined");
}
